import React, { useState, useEffect } from "react";

interface CurrencyInputProps {
  valor: number; // Valor em centavos
  onValorChange: (valor: number) => void; // Recebe valor em centavos
  onBlur: () => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  valor,
  onValorChange,
  onBlur,
  onKeyDown,
}) => {
  // Estado para controlar o texto exibido no input
  const [displayValue, setDisplayValue] = useState("");

  // Atualiza o displayValue quando o valor em centavos mudar
  useEffect(() => {
    // Garante que o valor é tratado como número
    const valorEmCentavos = isNaN(valor) ? 0 : valor;
    // Converte centavos para reais para exibição
    setDisplayValue(formatarEmReais(valorEmCentavos));
  }, [valor]);

  // Formata um valor em centavos para exibição em reais
  const formatarEmReais = (valorEmCentavos: number): string => {
    if (valorEmCentavos === 0) return "R$ 0,00";

    // Converte centavos para reais (divide por 100)
    const valorEmReais = valorEmCentavos / 100;

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valorEmReais);
  };

  // Converte o texto digitado para um valor em centavos
  const converterParaCentavos = (texto: string): number => {
    // Remove todos os caracteres não numéricos
    const apenasNumeros = texto.replace(/\D/g, "");
    return parseInt(apenasNumeros || "0", 10);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    // Converte o valor digitado para centavos
    const valorEmCentavos = converterParaCentavos(inputValue);

    valor = valorEmCentavos;
    // Atualiza o estado com o formato visual em reais
    setDisplayValue(formatarEmReais(valor));

    // Notifica o componente pai sobre a mudança, enviando o valor em centavos
    onValorChange(valorEmCentavos);
  };

  const handleBlur = async () => {
    // No blur, garantimos que o valor esteja formatado corretamente
    setDisplayValue(formatarEmReais(valor));

    onBlur();
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Permite apenas números, backspace, delete, setas e tab
    const allowed = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
    if (
      !allowed.includes(event.key) &&
      event.key !== "Backspace" &&
      event.key !== "Delete" &&
      event.key !== "ArrowLeft" &&
      event.key !== "ArrowRight" &&
      event.key !== "Tab"
    ) {
      event.preventDefault();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Passa o evento para o componente pai se onKeyDown for fornecido
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const getMaxWidth = () => {
    return Math.min(150, Math.max(80, displayValue.length * 9 + 15));
  };

  return (
    <>
      <input
        type="text"
        className="form-control w-full max-w-[120px] text-right bg-white border-2 border-[#3f83f8] rounded-xl px-2 py-1.5 outline-none text-[#3f83f8] font-black shadow-lg shadow-blue-50"
        style={{
          minWidth: getMaxWidth() + "px",
          Width: getMaxWidth() + "px",
          textAlign: "right",
          paddingRight: "5px",
        }}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        onKeyDown={handleKeyDown}
        autoFocus
      />
    </>
  );
};

export default CurrencyInput;
